import { en } from '@/i18n/locales/en';

/**
 * French translation resources. Typed as `typeof en` so its shape must match
 * the English bundle exactly — a missing or misspelled key fails compilation.
 */
export const fr: typeof en = {
  nav: {
    dashboard: 'Tableau de bord',
    guests: 'Clients',
    bookings: 'Réservations',
    rooms: 'Chambres',
    reports: 'Rapports',
    channels: 'Canaux',
    myProfile: 'Mon profil',
    addProperty: 'Ajouter un établissement',
    propertyProfile: "Profil de l'établissement",
    members: 'Membres et invitations',
    dataCleanup: 'Nettoyage des données',
    verifyReceipt: 'Vérifier un reçu',
    notifications: 'Notifications',
    auditLog: "Journal d'audit",
    platformAdmin: 'Administration plateforme',
  },
  notifications: {
    title: 'Notifications',
    subtitle: 'Activité des réservations, paiements et finances de cet espace.',
    bellAria: 'Notifications, {{count}} non lues',
    markAllRead: 'Tout marquer comme lu',
    loading: 'Chargement des notifications…',
    error: 'Une erreur est survenue lors du chargement des notifications.',
    empty: 'Aucune notification pour le moment.',
    byActor: 'par {{name}}',
    openBooking: 'Ouvrir {{code}}',
    noDetails: 'Aucun détail supplémentaire.',
    pageOf: 'Page {{page}} sur {{total}}',
    columns: {
      field: 'Champ',
      before: 'Avant',
      after: 'Après',
    },
    events: {
      booking_created: 'Réservation créée',
      booking_updated: 'Réservation modifiée',
      booking_cancelled: 'Réservation annulée',
      booking_deleted: 'Réservation supprimée',
      payment_recorded: 'Paiement enregistré',
      payment_updated: 'Paiement modifié',
      payment_deleted: 'Paiement supprimé',
      financial_updated: 'Dossier financier modifié',
      unknown: 'Activité',
    },
    fields: {
      check_in_date: "Date d'arrivée",
      check_out_date: 'Date de départ',
      estimated_check_in_time: "Heure d'arrivée",
      estimated_check_out_time: 'Heure de départ',
      adults: 'Adultes',
      children: 'Enfants',
      room_id: 'Chambre',
      booking_channel_id: 'Canal',
      status: 'Statut',
      confirmed: 'Confirmée',
      cancelled: 'Annulée',
      stay_purpose: 'Motif du séjour',
      amount: 'Montant',
      type: 'Type',
      method: 'Mode',
      received_at: 'Reçu le',
      expected_amount: 'Montant attendu',
      actual_amount: 'Montant convenu',
      notes: 'Note',
    },
  },
  shell: {
    greetingMorning: 'Bonjour',
    greetingAfternoon: 'Bon après-midi',
    greetingEvening: 'Bonsoir',
    signOut: 'Se déconnecter',
    offlineBanner: "Vous êtes hors ligne. Vous pouvez continuer à travailler — vos modifications seront synchronisées automatiquement au retour de la connexion.",
    refresh: "Actualiser l'application",
    back: 'Précédent',
    forward: 'Suivant',
    collapseNav: 'Réduire la navigation',
    expandNav: 'Afficher la navigation',
    openMenu: 'Ouvrir le menu de navigation',
    closeMenu: 'Fermer le menu de navigation',
    checkingSession: 'Vérification de votre session…',
    subtitle: "Console d'exploitation",
    primaryNav: 'Principal',
    adminNav: 'Administration',
    selectedProperty: 'Établissement sélectionné',
    property: 'Établissement',
    cachedData: 'Données en cache',
    statusOffline: 'Hors ligne',
    statusSyncing: 'Synchronisation',
    statusConflicts_one: '{{count}} conflit',
    statusConflicts_other: '{{count}} conflits',
    statusFailed: '{{count}} en échec',
    statusQueued: '{{count}} en attente',
    dismissInstallBanner: "Masquer la bannière d'installation",
  },
  camera: {
    title: 'Prendre une photo',
    preview: 'Aperçu de la caméra',
    switch: 'Changer de caméra',
    capture: 'Capturer',
    saving: 'Enregistrement…',
    errors: {
      blocked: "L'accès à la caméra a été bloqué. Autorisez-le dans votre navigateur puis réessayez.",
      notFound: "Aucune caméra n'a été trouvée sur cet appareil.",
      inUse: 'La caméra est déjà utilisée par une autre application.',
      generic: "Impossible de démarrer la caméra. Veuillez réessayer.",
      notReady: "La caméra n'est pas encore prête. Patientez un instant puis réessayez.",
      captureFailed: "Impossible de capturer la photo sur cet appareil.",
      captureRetry: 'Impossible de capturer la photo. Veuillez réessayer.',
    },
  },
  savedFilters: {
    label: 'Filtre enregistré',
    none: 'Aucun filtre enregistré',
    apply: 'Appliquer',
    name: 'Nom du filtre',
    save: 'Enregistrer',
    update: 'Mettre à jour le filtre sélectionné',
    delete: 'Supprimer le filtre sélectionné',
    createdToast: 'Filtre enregistré.',
    updatedToast: 'Filtre mis à jour.',
    deletedToast: 'Filtre supprimé.',
    createError: "Impossible d'enregistrer ce filtre.",
    updateError: 'Impossible de mettre à jour ce filtre.',
    deleteError: 'Impossible de supprimer ce filtre.',
  },
  guestSearch: {
    label: 'Client',
    noMatches: 'Aucun client correspondant',
    typeToSearch: 'Saisissez pour rechercher un client',
  },
  language: {
    label: 'Langue',
    english: 'English',
    french: 'Français',
  },
  home: {
    login: 'Connexion',
    register: 'Inscription',
    heroTitle: 'Gérez chaque séjour depuis un espace de travail sécurisé.',
    heroSubtitle:
      "Conçu pour les hôtels, maisons d'hôtes, lodges et hébergements de courte durée qui ont besoin de réservations, clients, paiements, avis et accès du personnel au même endroit.",
    heroRegisterCta: "Créer l'espace de l'établissement",
    heroLoginCta: "Se connecter à l'espace",
    closeMenu: 'Fermer le menu de navigation',
    stepAria: 'Étape {{number}} : {{title}}',
    theme: 'Thème',
    mobileNav: 'Navigation mobile',
    onboarding: {
      eyebrow: 'Processus de démarrage',
      description:
        "Le parcours initial reste explicite : vérification du code e-mail, création d'un accès sécurisé, enregistrement de l'établissement, puis exploitation quotidienne.",
      title: "De l'enregistrement de l'établissement à l'exploitation, en quatre étapes",
      step1Title: "Vérifier l'e-mail de l'administrateur",
      step1Body: "Saisissez une adresse e-mail et confirmez le code d'inscription valable 5 minutes.",
      step2Title: 'Créer un accès sécurisé',
      step2Body: 'Complétez le profil du compte avec un numéro de téléphone et un mot de passe robuste.',
      step3Title: "Enregistrer l'espace de l'établissement",
      step3Body: "Renseignez le profil de l'établissement ou acceptez l'invitation d'une équipe existante.",
      step4Title: 'Lancer les opérations',
      step4Body: 'Invitez votre personnel et gérez les séjours, les clients, les paiements et les avis.',
    },
    value: {
      eyebrow: 'Proposition de valeur',
      title: 'Une plateforme plus sereine pour les équipes hôtelières modernes',
      description:
        'Nang Book réunit séjours, clients, paiements, accès et traçabilité dans un seul espace cloud.',
      prop1Title: 'Gérez chaque séjour depuis une seule fiche',
      prop1Body: "Réservations, informations client, préparation de l'arrivée et avis dans une seule vue.",
      prop2Title: 'Adapté à tous les types d’établissement',
      prop2Body: 'Hôtels, maisons d’hôtes, lodges et courts séjours travaillent sur la même plateforme.',
      prop3Title: 'Protégez les finances et la traçabilité',
      prop3Body: 'Statut des paiements, actions sensibles et responsabilité sont encadrés par des contrôles stricts.',
    },
    access: {
      resetPassword: 'Réinitialiser le mot de passe',
      resendReset: 'Renvoyer le lien de réinitialisation',
      resendVerification: 'Renvoyer la vérification',
      registerProperty: 'Enregistrer un établissement',
    },
    signals: {
      stays: 'Séjours',
      staysHelper: "aujourd'hui",
      payments: 'Paiements',
      paymentsValue: 'À jour',
      paymentsHelper: 'vue finance',
      access: 'Accès',
      accessValue: 'Restreint',
      accessHelper: 'par rôle',
      bookingSync: 'Synchronisation des réservations',
      financeReview: 'Revue financière',
      auditTrail: "Journal d'audit",
    },
    testimonials: {
      eyebrow: 'Paroles de clients',
      title: 'Adopté par des équipes de tous types d’établissements',
      description:
        'Hôtels, maisons d’hôtes, lodges et opérateurs de courts séjours utilisent Nang Book pour y voir clair au quotidien.',
      label: 'Témoignage',
      region: 'Témoignages clients',
      stars: 'Témoignage cinq étoiles',
      position: '{{current}} sur {{total}}',
      previous: 'Témoignage précédent',
      next: 'Témoignage suivant',
      show: 'Afficher le témoignage de {{name}}',
      role1: 'Directrice générale, Accra City Suites',
      quote1:
        'Nang Book donne à la réception, à la finance et à l’entretien la même vision de chaque séjour, sans courir après des tableurs.',
      role2: 'Propriétaire, Harbor Lane Guest House',
      quote2:
        'Le parcours de démarrage indique clairement quoi faire en premier. Nous nous sommes inscrits, avons vérifié les accès, et toute l’équipe travaille depuis un seul tableau de bord.',
      role3: 'Responsable des opérations, Enugu Short Stays',
      quote3:
        'Les fiches clients, les paiements et les accès du personnel sont bien organisés. Les passations de service se font sans rien oublier.',
      role4: 'Directeur, Northgate Lodge',
      quote4:
        'Les parcours de récupération et de vérification sont assez simples pour le personnel saisonnier, tout en restant sous contrôle de la direction.',
      role5: 'Fondatrice, Rift Valley Retreats',
      quote5:
        'Nous gérons plusieurs petits établissements, et Nang Book garde chaque équipe concentrée sur les arrivées, les clients et la responsabilité.',
    },
  },
  common: {
    genericError: 'Une erreur est survenue. Veuillez réessayer.',
    login: 'Connexion',
    print: 'Imprimer',
    export: 'Exporter',
    exporting: 'Exportation…',
    exportCsv: 'Exporter en CSV',
    exportXlsx: 'Exporter en XLSX',
    cancel: 'Annuler',
    save: 'Enregistrer',
    saving: 'Enregistrement…',
    saveChanges: 'Enregistrer les modifications',
    edit: 'Modifier',
    add: 'Ajouter',
    delete: 'Supprimer',
    deleting: 'Suppression…',
    clear: 'Effacer',
    back: 'Précédent',
    next: 'Suivant',
    previous: 'Précédent',
    yes: 'Oui',
    no: 'Non',
    close: 'Fermer',
    actions: 'Actions',
    actionsForAria: 'Actions pour {{name}}',
    unassigned: 'Non attribuée',
    emptyListAction: 'Cette action ne peut pas être effectuée sur une liste vide.',
    nothingToPrint: 'Il n\'y a rien à imprimer.',
    correctFields: 'Veuillez corriger les champs en surbrillance.',
    optional: 'Facultatif.',
    phoneHelper: 'Choisissez le pays, puis saisissez le numéro.',
  },
  field: {
    email: 'E-mail',
    password: 'Mot de passe',
    newPassword: 'Nouveau mot de passe',
    confirmPassword: 'Confirmer le mot de passe',
    confirmNewPassword: 'Confirmer le nouveau mot de passe',
    fullName: 'Nom complet',
    country: 'Pays',
    mobileNumber: 'Numéro de téléphone',
    whatsappNumber: 'Numéro WhatsApp',
    verificationCode: 'Code de vérification',
    placeOfOrigin: 'Lieu d\'origine',
    nationality: 'Nationalité',
    gender: 'Genre',
    dateOfBirth: 'Date de naissance',
    primaryPhone: 'Téléphone principal',
    secondaryPhone: 'Téléphone secondaire',
    guestCode: 'Code client',
    phone: 'Téléphone',
    status: 'Statut',
    checkIn: 'Arrivée',
    checkOut: 'Départ',
    bookingCode: 'Code de réservation',
    guest: 'Client',
    channel: 'Canal',
    room: 'Chambre',
    checkInDate: "Date d'arrivée",
    checkOutDate: 'Date de départ',
    estCheckInTime: "Heure d'arrivée estimée",
    estCheckOutTime: 'Heure de départ estimée',
    adults: 'Adultes',
    children: 'Enfants',
    expectedAmount: 'Montant prévu',
    bookingChannel: 'Canal de réservation',
    stayPurpose: 'Motif du séjour',
    confirmed: 'Confirmée',
    cancelled: 'Annulée',
    from: 'Du',
    to: 'Au',
    notes: 'Notes',
    amount: 'Montant',
    balance: 'Solde',
    comment: 'Commentaire',
    name: 'Nom',
    role: 'Rôle',
    expires: 'Expire',
    mobile: 'Mobile',
    whatsapp: 'WhatsApp',
    address: 'Adresse',
    website: 'Site web',
    codePrefix: 'Préfixe de code',
    contactPerson: 'Personne à contacter',
    contactNumber: 'Numéro de contact',
  },
  documents: {
    fields: {
      chooseFile: 'Choisir un fichier',
      takePhoto: 'Prendre une photo',
      documentType: 'Type de document',
      documentNumber: 'Numéro du document',
      issuingCountry: 'Pays de délivrance',
      issuingAuthority: 'Autorité de délivrance',
      issueDate: 'Date de délivrance',
      expiryDate: "Date d'expiration",
    },
  },
  enum: {
    status: {
      pending: 'En attente',
      confirmed: 'Confirmée',
      checked_in: 'Enregistrée',
      checked_out: 'Départ effectué',
      cancelled: 'Annulée',
      no_show: 'Non présentée',
    },
    stayPurpose: {
      vacation: 'Vacances',
      event: 'Événement',
      accommodation: 'Hébergement',
      business: 'Affaires',
      family_visit: 'Visite familiale',
      other: 'Autre',
    },
    paymentMethod: {
      cash: 'Espèces',
      mobile_pay: 'Paiement mobile',
      card: 'Carte',
      bank_transfer: 'Virement bancaire',
      paypal: 'PayPal',
    },
    paymentType: {
      charge: 'Encaissement',
      refund: 'Remboursement',
    },
    documentType: {
      passport: 'Passeport',
      national_id: "Carte nationale d'identité",
      drivers_license: 'Permis de conduire',
      residence_permit: 'Titre de séjour',
      other: 'Autre',
    },
    paymentStatus: {
      unpaid: 'Impayé',
      partially_paid: 'Partiellement payé',
      paid: 'Payé',
      refunded: 'Remboursé',
      overpaid: 'Trop-perçu',
    },
    commentType: {
      complaint: 'Réclamation',
      feedback: 'Retour',
      internal_note: 'Note interne',
      payment_note: 'Note de paiement',
      maintenance_note: 'Note de maintenance',
      reservation_note: 'Note de réservation',
    },
    tokenAction: {
      view_booking: 'Voir la réservation',
      submit_review: 'Soumettre un avis',
    },
    roomStatus: {
      available: 'Disponible',
      occupied: 'Occupée',
      cleaning: 'Nettoyage',
      maintenance: 'Maintenance',
      out_of_service: 'Hors service',
    },
    role: {
      systemAdministrator: 'Administrateur système',
      manager: 'Gestionnaire',
      frontDeskStaff: 'Personnel de réception',
      financeUser: 'Utilisateur finance',
    },
    membershipStatus: {
      active: 'actif',
      invited: 'invité',
      disabled: 'désactivé',
    },
  },
  auth: {
    login: {
      title: 'Connexion',
      subtitle: 'Utilisez votre compte personnel pour continuer.',
      submit: 'Se connecter',
      submitting: 'Connexion…',
      signedIn: 'Connexion réussie.',
      newHere: 'Nouveau ?',
      createAccount: 'Créer un compte',
      forgotPassword: 'Mot de passe oublié ?',
      requestReset: 'Demander une réinitialisation',
      needVerification: "Besoin d'un e-mail de vérification ?",
      resendVerification: 'Renvoyer la vérification',
    },
    register: {
      title: 'Créez votre compte',
      subtitle: 'Commencez par vérifier votre e-mail.',
      emailHelper: 'Nous vous enverrons un code d\'inscription à usage unique.',
      sendCode: 'Envoyer le code',
      sendingCode: 'Envoi du code…',
      otpPrompt: 'Saisissez le code à 6 chiffres envoyé à {{email}}.',
      otpHelper: 'Les codes expirent après 5 minutes.',
      verifyCode: 'Vérifier le code',
      verifyingCode: 'Vérification du code…',
      resend: 'Renvoyer le code',
      resending: 'Renvoi…',
      resendCountdown: 'Renvoyer le code ({{seconds}} s)',
      changeEmail: 'Changer d\'e-mail',
      mobileHelper: 'Choisissez le pays, puis saisissez le numéro.',
      whatsappHelper: 'Par défaut, le numéro de téléphone est utilisé s\'il est laissé vide.',
      createAccount: 'Créer le compte',
      creatingAccount: 'Création du compte…',
      alreadyRegistered: 'Déjà inscrit ?',
      login: 'Connexion',
      otpSent: 'Si cet e-mail peut être enregistré, un code de vérification a été envoyé.',
      sendCodeError: 'Impossible d\'envoyer le code de vérification. Veuillez réessayer.',
      emailVerified: 'E-mail vérifié. Continuez la création de votre compte.',
      verifyCodeError: 'Impossible de vérifier ce code. Veuillez réessayer.',
      resendError: 'Impossible de renvoyer le code de vérification. Veuillez réessayer.',
      accountCreatedWelcome: 'Compte créé. Bienvenue sur Nang Book !',
      accountCreatedLogin: 'Compte créé. Veuillez vous connecter pour continuer.',
    },
    forgot: {
      title: 'Mot de passe oublié',
      subtitle: 'Demandez un lien de réinitialisation sécurisé pour votre compte.',
      submit: 'Envoyer le lien',
      submitting: 'Envoi du lien…',
      sent: 'Les instructions de réinitialisation ont été envoyées si l\'e-mail existe.',
      haveLink: 'Vous avez déjà un lien de réinitialisation ? Ouvrez-le depuis votre e-mail pour continuer.',
      didNotReceive: 'Vous ne l\'avez pas reçu ?',
      resend: 'Renvoyer le lien',
    },
    reset: {
      title: 'Réinitialiser le mot de passe',
      subtitle: 'Définissez un nouveau mot de passe depuis votre e-mail de réinitialisation.',
      submit: 'Réinitialiser le mot de passe',
      submitting: 'Réinitialisation…',
      done: 'Mot de passe réinitialisé. Vous pouvez maintenant vous connecter.',
      missingDetails: 'Ce lien de réinitialisation est incomplet.',
      requestNew: 'Demandez un nouveau lien de réinitialisation et ouvrez-le depuis votre e-mail.',
      needNewLink: 'Besoin d\'un nouveau lien ?',
      resend: 'Renvoyer le lien',
    },
    resendVerification: {
      title: 'Renvoyer la vérification',
      subtitle: 'Envoyez un autre lien de vérification d\'e-mail à un administrateur.',
      submit: 'Renvoyer la vérification',
      submitting: 'Envoi de la vérification…',
      sent: 'Un nouvel e-mail de vérification a été envoyé si le compte existe.',
      alreadyVerified: 'E-mail déjà vérifié ?',
    },
    resendReset: {
      title: 'Renvoyer le lien de réinitialisation',
      subtitle: 'Envoyez un autre e-mail de réinitialisation à un compte du personnel.',
      submit: 'Renvoyer le lien',
      submitting: 'Renvoi du lien…',
      sent: 'Un nouveau lien de réinitialisation a été envoyé si l\'e-mail existe.',
      remembered: 'Vous vous souvenez du mot de passe ?',
    },
    verifyEmail: {
      title: 'Vérifier l\'e-mail',
      subtitle: 'Confirmez l\'e-mail de l\'administrateur avant d\'utiliser l\'espace de travail.',
      intro: 'Si vous avez ouvert cette page depuis un e-mail de vérification, continuez pour terminer la vérification. Si le lien a expiré, demandez un nouvel e-mail de vérification.',
      submit: 'Vérifier l\'e-mail',
      submitting: 'Vérification…',
      done: 'E-mail vérifié. Vous pouvez maintenant vous connecter.',
      needAnother: 'Besoin d\'un autre e-mail de vérification ?',
      resend: 'Renvoyer la vérification',
    },
  },
  dashboard: {
    title: 'Tableau de bord',
    subtitle: "Opérations de l'établissement aujourd'hui",
    loading: 'Chargement du tableau de bord…',
    error: 'Une erreur est survenue lors du chargement du tableau de bord. Veuillez réessayer.',
    welcome: 'Bienvenue',
    welcomeNamed: 'Bienvenue, {{name}}',
    metrics: {
      arrivalsToday: "Arrivées aujourd'hui",
      departuresToday: "Départs aujourd'hui",
      currentlyCheckedIn: 'Actuellement enregistrés',
      upcomingArrivals: 'Arrivées à venir (7 jours)',
      needsCleanup: 'À nettoyer',
      collectedThisMonth: 'Encaissé ce mois-ci',
      agreedThisMonth: 'Convenu ce mois-ci',
    },
    limited: {
      subtitle: 'Voici ce que vous pouvez consulter.',
      guestsDesc: 'Rechercher et gérer les fiches clients et les coordonnées.',
      bookingsDesc: 'Consulter et gérer les réservations, arrivées et départs.',
      open: 'Ouvrir {{label}}',
      noAccess: 'Utilisez le menu pour accéder aux sections auxquelles vous avez accès.',
    },
    getStarted: {
      subtitle: 'Préparons votre espace de travail. Créez un établissement pour débloquer votre tableau de bord.',
      createTitle: 'Créer votre établissement',
      createDesc: "Configurez votre hôtel, maison d'hôtes ou lodge pour gérer les réservations, clients, chambres et rapports.",
      createCta: "Créer l'établissement",
      profileTitle: 'Compléter votre profil',
      profileDesc: 'Mettez à jour votre nom et votre numéro de téléphone, et définissez un mot de passe fort.',
      profileCta: 'Modifier le profil',
      invite: 'Une fois votre établissement créé, vous pourrez inviter du personnel et attribuer des rôles depuis Membres et invitations.',
    },
  },
  guests: {
    title: 'Clients',
    subtitle: 'Fiches clients et historique de contact',
    refresh: 'Actualiser les clients',
    add: 'Ajouter un client',
    search: 'Rechercher des clients',
    searchPlaceholder: 'Nom, code client, e-mail ou téléphone',
    loading: 'Chargement des clients…',
    error: 'Une erreur est survenue lors du chargement des clients. Veuillez réessayer.',
    empty: 'Aucun client trouvé.',
    exportError: 'Impossible d\'exporter les clients. Veuillez réessayer.',
    newSubtitle: 'Le code client est généré automatiquement à l\'enregistrement.',
    createSubmit: 'Créer le client',
    creating: 'Création…',
    createdToast: 'Client {{code}} créé. Continuez avec sa réservation.',
    detailLoading: 'Chargement du client…',
    detailError: 'Une erreur est survenue lors du chargement de ce client. Veuillez réessayer.',
    updatedToast: 'Client mis à jour.',
    deletedToast: 'Client supprimé.',
    deleteError: 'Impossible de supprimer ce client. Veuillez réessayer.',
    bookingHistory: 'Historique des réservations',
    noBookings: 'Aucune réservation enregistrée pour ce client.',
    receipt: 'Reçu',
    getReceipt: 'Obtenir le reçu',
    getReceiptAria: 'Obtenir le reçu pour {{code}}',
    preparingReceipt: 'Préparation…',
    receiptDownloadError: 'Impossible de télécharger le reçu PDF.',
    deleteTitle: 'Supprimer ce client ?',
    deleteBody:
      'Ceci retire {{name}} des listes de clients actifs. L\'historique des réservations est conservé et cette action peut être annulée par un administrateur si nécessaire.',
    profile: {
      male: 'Homme',
      female: 'Femme',
      validation: {
        fullNameRequired: 'Le nom complet est obligatoire.',
        genderRequired: 'Le genre est obligatoire.',
        placeOfOriginRequired: "Le lieu d'origine est obligatoire.",
        nationalityRequired: 'La nationalité est obligatoire.',
        dateOfBirthRequired: 'La date de naissance est obligatoire.',
        countryFromList: 'Choisissez un pays dans la liste.',
        minimumAge: 'Le client doit avoir au moins {{years}} ans.',
      },
    },
    documents: {
      requiredForCheckIn:
        "Ce client ne peut pas être enregistré à l'arrivée tant que sa pièce d'identité n'est pas saisie — il manque : {{fields}}. Expliquez au client que nous sommes tenus de recueillir ces informations à des fins réglementaires.",
      fieldLabel: {
        document_type: 'type de document',
        document_number: 'numéro du document',
        issuing_country: 'pays de délivrance',
      },
      title: "Pièces d'identité",
      loading: 'Chargement des documents…',
      error: 'Une erreur est survenue lors du chargement des documents. Veuillez réessayer.',
      empty: "Aucune pièce d'identité n'a encore été téléversée.",
      submit: 'Soumettre',
      update: 'Mettre à jour',
      addTitle: "Ajouter une pièce d'identité",
      addHint:
        "Saisissez les informations du client à partir de l'image de sa pièce d'identité reçue lors de l'accueil. Joindre une image ici est facultatif.",
      editTitle: "Modifier la pièce d'identité",
      editHint:
        'Complétez ou corrigez les détails. Choisissez un nouveau fichier uniquement pour remplacer le fichier actuel.',
      replaceFile: 'Remplacer le fichier',
      savedToast: 'Document enregistré.',
      updatedToast: 'Document mis à jour.',
      deletedToast: 'Document supprimé.',
      saveError: "Impossible d'enregistrer le document. Veuillez réessayer.",
      updateError: 'Impossible de mettre à jour le document. Veuillez réessayer.',
      deleteError: 'Impossible de supprimer le document.',
      downloadError: 'Impossible de télécharger le document. Veuillez réessayer.',
      editAria: 'Modifier {{type}}',
      downloadAria: 'Télécharger {{type}}',
      deleteAria: 'Supprimer {{type}}',
      validation: {
        typeRequired: 'Le type de document est obligatoire.',
        expiryAfterIssue: "La date d'expiration doit être postérieure ou égale à la date de délivrance.",
      },
    },
  },
  bookings: {
    title: 'Réservations',
    subtitle: 'Fiches de séjour, filtrées par date, canal, statut et motif',
    refresh: 'Actualiser les réservations',
    add: 'Ajouter une réservation',
    search: 'Rechercher des réservations',
    searchPlaceholder: 'Code de réservation, client, chambre ou canal',
    loading: 'Chargement des réservations…',
    error: 'Une erreur est survenue lors du chargement des réservations. Veuillez réessayer.',
    empty: 'Aucune réservation ne correspond à ces filtres.',
    exportError: 'Impossible d\'exporter les réservations. Veuillez réessayer.',
    receiptGenerateError: 'Impossible de générer le reçu PDF.',
    receiptEmailError: 'Impossible d\'envoyer le reçu par e-mail.',
    receiptEmailedToast: 'Reçu envoyé par e-mail à {{recipient}}.',
    receiptShareTitle: 'Reçu {{code}}',
    receiptShareText: 'Votre reçu',
    receiptShareUnavailable: 'Le partage n\'est pas disponible ici ; le reçu a donc été téléchargé à la place.',
    receiptShareError: 'Impossible de partager le reçu.',
    receipt: 'Reçu',
    downloadPdf: 'Télécharger le PDF',
    emailToGuest: 'Envoyer au client',
    share: 'Partager',
    receiptAria: 'Reçu pour {{code}}',
    newTitle: 'Ajouter une réservation',
    newSubtitle: 'Le code de réservation est généré automatiquement à l\'enregistrement.',
    newSubtitleGuest:
      'Réservation pour {{name}} ({{code}}). Le code de réservation est généré automatiquement à l\'enregistrement.',
    detailLoading: 'Chargement de la réservation…',
    detailError: 'Une erreur est survenue lors du chargement de cette réservation. Veuillez réessayer.',
    updatedToast: 'Réservation mise à jour.',
    deletedToast: 'Réservation supprimée.',
    deleteError: 'Impossible de supprimer cette réservation. Veuillez réessayer.',
    deleteTitle: 'Supprimer cette réservation ?',
    deleteBody:
      'Ceci retire {{code}} des listes de réservations actives. La fiche est conservée pour l\'historique et peut être restaurée par un administrateur si nécessaire.',
    nightsOccupants: 'Nuits / occupants',
    nightsGuests: '{{nights}} nuits · {{guests}} clients',
    stayPurposeLabel: 'Motif du séjour',
    reviewAgreedAfterDateChange: 'Dates modifiées — vérifiez le montant convenu dans le dossier financier.',
    cancelBooking: 'Annuler la réservation',
    cancelling: 'Annulation…',
    cancelledToast: 'Réservation annulée. La chambre a été libérée.',
    cancelError: "Impossible d'annuler cette réservation. Veuillez réessayer.",
    cancelTitle: 'Annuler cette réservation ?',
    cancelBody:
      "Ceci libère la chambre de {{code}} et efface ses montants attendu, convenu et payé. Une réservation détenant encore de l'argent du client doit d'abord être intégralement remboursée.",
    form: {
      selectGuest: 'Sélectionnez un client pour cette réservation.',
      enterExpected: 'Saisissez le montant prévu à payer.',
      estCheckInTime: "Heure d'arrivée estimée",
      estCheckOutTime: 'Heure de départ estimée',
      expectedHelper: 'Prix affiché pour cette réservation (auto — comptabilité uniquement).',
      loadingSelectedChannel: 'Chargement du canal sélectionné…',
      roomHelperNone: 'Aucune chambre n\'est disponible pour ces dates. Vous pouvez enregistrer maintenant et attribuer une chambre plus tard.',
      roomHelperOptional: 'Facultatif. Seules les chambres libres pour les dates sélectionnées sont affichées.',
      currentlyAssignedRoom: 'Chambre actuellement attribuée',
      statusHelper: 'Par défaut « En attente » si laissé vide.',
      statusDefault: 'Par défaut',
    },
    wizard: {
      steps: {
        guest: 'Client',
        basic: 'Général',
        identity: 'Identité',
        stay: 'Séjour',
        finance: 'Finances',
        comment: 'Commentaire',
        review: 'Récapitulatif',
      },
      stepOf: 'Étape {{current}} sur {{total}}',
      loadingProperty: "Chargement des paramètres de l'établissement…",
      confirmBooking: 'Confirmer la réservation',
      createdToast: 'Réservation {{code}} créée.',
      saveError: 'Impossible d\'enregistrer cette réservation.',
      checking: 'Vérification…',
      stepCheckError: 'Impossible de vérifier cette étape. Veuillez réessayer.',
      completeRequired: 'Complétez les champs obligatoires avant de continuer.',
      completeRequiredSave: 'Complétez les champs obligatoires avant d\'enregistrer la réservation.',
      existingSelected: 'Client existant sélectionné : {{name}} ({{code}})',
      existingSelectedIncomplete:
        '{{name}} ({{code}}) est déjà enregistré, mais sa fiche est incomplète. Les deux étapes suivantes recueillent ce qui manque.',
      newProfileNote: 'Un nouveau profil client sera créé lors de la confirmation de cette réservation.',
      registerNewGuest: 'Enregistrer un nouveau client',
      fullLegalNames: 'Noms légaux complets',
      gender: 'Sexe',
      male: 'Homme',
      female: 'Femme',
      phoneNumber: 'Numéro de téléphone',
      alternativeNumber: 'Numéro alternatif',
      dateOfBirth: 'Date de naissance',
      uploadIdPhoto: "Téléverser la photo de la pièce d'identité",
      identityDetailsLaterNote:
        "Photographiez ou joignez la pièce d'identité maintenant — seul le type de document est obligatoire. Les autres détails peuvent être complétés plus tard depuis la fiche du client.",
      checkoutDate: 'Date de départ',
      reservationMedium: 'Moyen de réservation',
      allowedRange: 'Autorisé {{min}}–{{max}}',
      loadingChannels: 'Chargement des canaux…',
      loadingRooms: 'Chargement des chambres…',
      roomHelperNoneCreate: 'Aucune chambre n\'est disponible pour ces dates. Vous pouvez créer la réservation et attribuer une chambre plus tard.',
      perNight: '{{amount}}/nuit',
      purpose: 'Motif',
      comment: 'Commentaire',
      paymentType: 'Type de paiement',
      actualAmount: 'Montant réel',
      amountPaidNow: 'Montant payé maintenant',
      expectedHelperRoom: 'Tarif de la chambre × {{count}} nuits (modifiez le montant réel si négocié).',
      review: {
        identity: 'Identité',
        idPhoto: "Photo de la pièce d'identité",
        stay: 'Séjour',
        occupants: 'Occupants',
        expectedList: 'Montant prévu (affiché)',
        agreedDue: 'Montant convenu (dû)',
        paidNow: 'Payé maintenant',
        occupantsValue: '{{adults}} adultes, {{children}} enfants',
        stayValue: '{{checkIn}} {{checkInTime}} au {{checkOut}} {{checkOutTime}}',
        existingGuestRecord: 'Fiche client existante',
        onExistingRecord: 'Sur la fiche client existante',
        attached: 'Jointe ({{name}})',
        notAttached: 'Non jointe',
        identityValue: '{{type}} {{number}}',
      },
      validation: {
        selectGuestOrRegister: 'Sélectionnez un client existant ou enregistrez un nouveau client.',
        fullLegalNamesRequired: 'Les noms légaux complets sont obligatoires.',
        genderRequired: 'Le sexe est obligatoire.',
        placeOfOriginRequired: "Le lieu d'origine est obligatoire.",
        nationalityRequired: 'La nationalité est obligatoire.',
        dateOfBirthRequired: 'La date de naissance est obligatoire.',
        idTypeRequired: "Le type de pièce d'identité est obligatoire.",
        expiryAfterIssue: "La date d'expiration doit être postérieure ou égale à la date de délivrance.",
        adultsRequired: 'Le nombre d\'adultes est obligatoire.',
        childrenRequired: 'Le nombre d\'enfants est obligatoire.',
        checkInDateRequired: "La date d'arrivée est obligatoire.",
        estCheckInTimeRequired: "L'heure d'arrivée estimée est obligatoire.",
        checkoutDateRequired: 'La date de départ est obligatoire.',
        estCheckoutTimeRequired: "L'heure de départ estimée est obligatoire.",
        reservationMediumRequired: 'Le moyen de réservation est obligatoire.',
        purposeRequired: 'Le motif est obligatoire.',
        atLeastOneAdult: 'Au moins un adulte est requis.',
        childrenNegative: 'Le nombre d\'enfants ne peut pas être négatif.',
        checkoutAfterCheckin: "La date de départ doit être postérieure à la date d'arrivée.",
        paymentTypeRequired: 'Le type de paiement est obligatoire.',
        expectedRequired: 'Le montant prévu est obligatoire.',
        actualRequired: 'Le montant réel est obligatoire.',
        amountPaidNowRequired: 'Le montant payé maintenant est obligatoire.',
        expectedNegative: 'Le montant prévu ne peut pas être négatif.',
        actualNegative: 'Le montant réel ne peut pas être négatif.',
        amountPaidNowNegative: 'Le montant payé maintenant ne peut pas être négatif.',
        amountPaidExceedsActual: 'Le montant payé maintenant ne peut pas être supérieur au montant réel.',
        timeRangeCheckIn: "L'heure d'arrivée estimée doit être comprise entre {{start}} et {{end}}.",
        timeRangeCheckout: "L'heure de départ estimée doit être comprise entre {{start}} et {{end}}.",
      },
    },
    panels: {
      financial: {
        title: 'Dossier financier',
        noPermission: "Vous n'avez pas la permission de consulter les données financières.",
        loading: 'Chargement du dossier financier…',
        error: 'Une erreur est survenue lors du chargement du dossier financier.',
        empty: 'Aucun dossier financier pour le moment.',
        expectedList: 'Montant prévu (prix affiché)',
        agreedDue: 'Montant convenu (total dû)',
        amountPaid: 'Montant payé',
        paymentStatus: 'Statut de paiement',
        discountServiceFee: 'Remise / frais de service',
        expectedAmountCurrency: 'Montant prévu ({{currency}})',
        expectedHelper: 'Prix affiché auto — comptabilité uniquement.',
        agreedAmountCurrency: 'Montant convenu ({{currency}})',
        agreedHelper: 'Total dû — ne peut pas être inférieur au montant déjà payé.',
        statusOverride: 'Forcer le statut de paiement',
        statusOverrideHelper: "Laissez vide pour laisser le système le déduire des montants ; seul « Remboursé » est une désignation manuelle.",
        deriveAuto: 'Déduire automatiquement',
        saveRecord: 'Enregistrer le dossier financier',
        savedToast: 'Dossier financier enregistré.',
        saveError: "Impossible d'enregistrer le dossier financier.",
      },
      payments: {
        title: 'Paiements',
        noPermission: "Vous n'avez pas la permission de consulter les paiements.",
        record: 'Enregistrer un paiement',
        editTitle: 'Modifier le paiement',
        totalDue: 'Total dû',
        amountRefunded: 'Montant remboursé',
        fullyPaid: 'Cette réservation est entièrement payée — aucun paiement supplémentaire ne peut être ajouté.',
        loading: 'Chargement des paiements…',
        error: 'Une erreur est survenue lors du chargement des paiements.',
        empty: 'Aucun paiement enregistré pour le moment.',
        method: 'Méthode',
        type: 'Type',
        receivedBy: 'Reçu par',
        receivedAt: 'Reçu le',
        editAria: 'Modifier le paiement {{id}}',
        deleteAria: 'Supprimer le paiement {{id}}',
        amountCurrency: 'Montant ({{currency}})',
        cannotExceed: 'Ne peut pas dépasser les {{amount}} encore dus.',
        outstanding: 'Solde restant : {{amount}}.',
        refundable: 'Au plus {{amount}} peut être remboursé.',
        receivedAtHelper: 'Par défaut, maintenant si laissé vide.',
        receivedByHelper: "Qui a encaissé cet argent. Par défaut, vous.",
        meDefault: 'Moi (par défaut)',
        reference: 'Référence',
        recordedToast: 'Paiement enregistré.',
        updatedToast: 'Paiement mis à jour.',
        saveError: "Impossible d'enregistrer ce paiement.",
        deletedToast: 'Paiement supprimé.',
        deleteError: 'Impossible de supprimer ce paiement.',
        deleteTitle: 'Supprimer ce paiement ?',
        deleteBody: 'Ceci supprime le paiement et recalcule le dossier financier de la réservation.',
      },
      review: {
        title: 'Avis',
        noPermission: "Vous n'avez pas la permission de consulter les avis.",
        sendRequest: "Envoyer une demande d'avis",
        sending: 'Envoi…',
        loading: "Chargement de l'avis…",
        error: "Une erreur est survenue lors du chargement de l'avis.",
        empty: 'Aucun avis soumis pour le moment.',
        marked: 'Marqué comme évalué',
        notMarked: 'Pas encore marqué comme évalué',
        rating: 'Note',
        markReviewed: 'Marquer comme évalué',
        saveReview: "Enregistrer l'avis",
        requestEmailed: "Demande d'avis envoyée par e-mail au client.",
        requestLinkOnly: "Lien de demande d'avis créé, mais l'e-mail n'a pas pu être envoyé — partagez le lien manuellement.",
        requestError: "Impossible d'envoyer une demande d'avis.",
        savedToast: 'Avis enregistré.',
        saveError: "Impossible d'enregistrer l'avis.",
      },
      comments: {
        title: 'Commentaires',
        noPermission: "Vous n'avez pas la permission de consulter les commentaires.",
        loading: 'Chargement des commentaires…',
        error: 'Une erreur est survenue lors du chargement des commentaires.',
        empty: 'Aucun commentaire pour le moment.',
        unknown: 'Inconnu',
        enterComment: 'Saisissez un commentaire avant de soumettre.',
        addedToast: 'Commentaire ajouté.',
        addError: "Impossible d'ajouter le commentaire.",
        type: 'Type',
        addPlaceholder: 'Ajouter un commentaire',
        adding: 'Ajout…',
        add: 'Ajouter le commentaire',
      },
      links: {
        title: 'Liens client de la réservation',
        noPermission: "Vous n'avez pas la permission de gérer les liens de réservation.",
        create: 'Créer un lien',
        loading: 'Chargement des liens…',
        error: 'Une erreur est survenue lors du chargement des liens de réservation.',
        empty: 'Aucun lien client créé pour cette réservation pour le moment.',
        revoked: 'Révoqué',
        active: 'Actif',
        expired: 'Expiré',
        expires: 'Expire le {{date}}',
        revoking: 'Révocation…',
        revoke: 'Révoquer',
        createTitle: 'Créer un lien client de réservation',
        oncePrompt: "Ce lien n'est affiché qu'une seule fois — copiez-le maintenant et partagez-le avec le client.",
        linkLabel: 'Lien',
        copyLink: 'Copier le lien',
        expiresInDays: 'Expire dans (jours)',
        expiryHelper: "Laissez vide pour utiliser l'expiration par défaut.",
        creating: 'Création…',
        createError: 'Impossible de créer un lien de réservation.',
        revokedToast: 'Lien révoqué.',
        revokeError: 'Impossible de révoquer ce lien.',
        copiedToast: 'Lien copié dans le presse-papiers.',
        copyError: 'Impossible de copier le lien automatiquement — sélectionnez-le et copiez-le manuellement.',
      },
      receipts: {
        title: 'Reçu',
        title403: 'Reçus',
        noPermission: "Vous n'avez pas la permission de consulter les documents de facturation.",
        preparing: 'Préparation…',
        download: 'Télécharger le reçu',
        email: 'Envoyer le reçu par e-mail',
        loading: 'Chargement du reçu…',
        error: 'Une erreur est survenue lors du chargement du reçu.',
        empty: "Aucun reçu n'a encore été émis. Utilisez « Télécharger le reçu » ou « Envoyer le reçu par e-mail » pour le générer.",
        number: 'Numéro',
        issuedBy: 'Émis par',
        issuedAt: 'Émis le',
        downloadAria: 'Télécharger {{number}} en PDF',
        emailAria: 'Envoyer {{number}} au client par e-mail',
        shareAria: 'Partager {{number}}',
        deleteAria: 'Supprimer {{number}}',
        deletedToast: 'Reçu supprimé.',
        deleteError: 'Impossible de supprimer ce reçu.',
        deleteTitle: 'Supprimer {{number}} ?',
        deleteBody: 'Ceci retire le reçu de cette réservation. Il sera régénéré au prochain téléchargement ou lors d\'une modification de paiement.',
      },
    },
  },
  rooms: {
    title: 'Chambres',
    subtitle: "Gérez l'inventaire des chambres, le statut d'exploitation, la capacité, les tarifs de base et les tarifs de vente datés.",
    refresh: 'Actualiser les chambres',
    add: 'Ajouter une chambre',
    search: 'Rechercher des chambres',
    allStatuses: 'Tous les statuts',
    loading: 'Chargement des chambres…',
    error: 'Une erreur est survenue lors du chargement des chambres. Veuillez réessayer.',
    empty: 'Aucune chambre ne correspond à ces filtres.',
    createdToast: 'Chambre créée.',
    updatedToast: 'Chambre mise à jour.',
    deletedToast: 'Chambre supprimée.',
    saveError: "Impossible d'enregistrer cette chambre.",
    deleteError: 'Impossible de supprimer cette chambre.',
    deleteConflict: 'Cette chambre est attribuée à des réservations actives. Retirez ces réservations ou mettez la chambre hors service.',
    addTitle: 'Ajouter une chambre',
    editTitle: 'Modifier {{number}}',
    deleteTitle: 'Supprimer {{number}} ?',
    deleteBody: "Ceci retire la chambre de l'inventaire actif. Elle ne peut être supprimée que si aucune réservation active ne la référence.",
    roomNumber: 'Numéro de chambre',
    roomType: 'Type de chambre',
    displayName: "Nom d'affichage",
    floor: 'Étage',
    baseRateCurrency: 'Tarif de base ({{currency}})',
    baseRate: 'Tarif de base',
    room: 'Chambre',
    type: 'Type',
    capacity: 'Capacité',
    viewScheduleAria: 'Voir le planning de {{number}}',
    manageRatesAria: 'Gérer les tarifs de {{number}}',
    viewSchedule: 'Voir le planning',
    manageRates: 'Gérer les tarifs',
    editAria: 'Modifier {{number}}',
    deleteAria: 'Supprimer {{number}}',
    rates: {
      title: 'Tarifs de {{number}}',
      rateName: 'Nom du tarif',
      amountCurrency: 'Montant ({{currency}})',
      starts: 'Début',
      ends: 'Fin',
      active: 'Actif',
      addRate: 'Ajouter un tarif',
      saveRate: 'Enregistrer le tarif',
      cancelEdit: 'Annuler la modification',
      loading: 'Chargement des tarifs…',
      error: 'Une erreur est survenue lors du chargement des tarifs.',
      empty: 'Aucun tarif enregistré pour cette chambre pour le moment.',
      caption: 'Tarifs de la chambre',
      datesHeader: 'Dates',
      dates: '{{start}} au {{end}}',
      any: 'Indifférent',
      activeChip: 'Actif',
      inactiveChip: 'Inactif',
      editAria: 'Modifier {{name}}',
      deleteAria: 'Supprimer {{name}}',
      createdToast: 'Tarif de chambre créé.',
      updatedToast: 'Tarif de chambre mis à jour.',
      deletedToast: 'Tarif de chambre supprimé.',
      saveError: "Impossible d'enregistrer ce tarif de chambre.",
      deleteError: 'Impossible de supprimer ce tarif de chambre.',
    },
    detail: {
      loading: 'Chargement du planning de la chambre…',
      error: 'Une erreur est survenue lors du chargement de cette chambre. Veuillez réessayer.',
      allRooms: 'Toutes les chambres',
      roomTitle: 'Chambre {{number}}',
      subtitleFallback: 'Planning et disponibilité de la chambre',
      refresh: 'Actualiser le planning',
      guests: '{{count}} clients',
      availabilityCalendar: 'Calendrier de disponibilité',
      upcomingBookings: 'Réservations à venir',
      noUpcoming: 'Aucune réservation à venir pour cette chambre.',
      caption: 'Réservations à venir pour la chambre {{number}}',
      booking: 'Réservation',
      nights: 'Nuits',
      prevMonth: 'Mois précédent',
      nextMonth: 'Mois suivant',
      available: 'Disponible',
      occupied: 'Occupée',
      today: "Aujourd'hui",
      cellAria: '{{date}} : {{state}}',
      nights_one: '{{count}} nuit',
      nights_other: '{{count}} nuits',
      notAvailable: "Cette chambre est {{status}} et n'est pas disponible à la réservation.",
      availableNowNoBookings: 'Disponible maintenant, sans réservation à venir.',
      availableFromOnward: 'Disponible à partir du {{date}}.',
      availableWindow: "Disponible {{when}} pour un maximum de {{nights}} — le prochain client arrive le {{date}}.",
      whenNow: 'maintenant',
      whenOn: 'le {{date}}',
    },
  },
  settings: {
    channels: {
      title: 'Canaux de réservation',
      subtitle: 'Gérez les sources dont peuvent provenir les réservations. Désactivez un canal pour le masquer des nouvelles réservations sans perdre les enregistrements historiques.',
      refresh: 'Actualiser les canaux',
      add: 'Ajouter un canal',
      loading: 'Chargement des canaux de réservation…',
      error: 'Une erreur est survenue lors du chargement des canaux de réservation. Veuillez réessayer.',
      empty: 'Aucun canal de réservation pour le moment. Ajoutez-en un pour commencer.',
      activeChip: 'Actif',
      inactiveChip: 'Inactif',
      editAria: 'Modifier {{name}}',
      deleteAria: 'Supprimer {{name}}',
      addTitle: 'Ajouter un canal de réservation',
      editTitle: 'Modifier le canal de réservation',
      active: 'Actif',
      createdToast: 'Canal de réservation créé.',
      updatedToast: 'Canal de réservation mis à jour.',
      deletedToast: 'Canal de réservation supprimé.',
      saveError: "Impossible d'enregistrer ce canal de réservation.",
      deleteError: 'Impossible de supprimer ce canal de réservation.',
      deleteConflict: "Ce canal est encore utilisé par des réservations existantes et ne peut pas être supprimé. Désactivez-le plutôt pour le masquer des nouvelles réservations tout en conservant les enregistrements historiques.",
      deleteTitle: 'Supprimer {{name}} ?',
      deleteBody: 'Ceci supprime définitivement le canal. Il ne peut être supprimé que si aucune réservation ne le référence actuellement.',
    },
    property: {
      noneSelected: "Aucun établissement sélectionné. Choisissez d'abord un établissement.",
      loading: "Chargement du profil de l'établissement…",
      error: "Une erreur est survenue lors du chargement du profil de l'établissement. Veuillez réessayer.",
      title: "Profil de l'établissement",
      subtitle: 'Détails du compte et de contact pour {{name}}.',
      updatedToast: "Profil de l'établissement mis à jour.",
      saveError: "Impossible d'enregistrer le profil de l'établissement.",
      logo: 'Logo',
      logoAlt: 'Logo de {{name}}',
      logoPreviewError: "Le logo n'a pas pu être prévisualisé.",
      logoLoading: "Chargement de l'aperçu du logo…",
      logoNone: "Aucun logo n'a encore été téléversé.",
      replaceLogo: 'Remplacer le logo',
      uploadLogo: 'Téléverser un logo',
      removeLogo: 'Retirer le logo',
      logoUploadedToast: 'Logo téléversé.',
      logoUploadError: 'Impossible de téléverser le logo.',
      logoRemovedToast: 'Logo retiré.',
      logoRemoveError: 'Impossible de retirer le logo.',
      propertyName: "Nom de l'établissement",
      contactPerson: 'Personne à contacter',
      contactPersonTitle: 'Titre de la personne à contacter',
      contactNumber: 'Numéro de contact',
      contactEmail: 'E-mail de contact',
      contactEmailHelper: "Affiché sur les reçus comme contact de l'établissement.",
      currency: 'Devise',
      currencyHelper: 'Utilisée pour chaque montant de cet établissement.',
      receiptLanguage: 'Langue du reçu',
      receiptLanguageHelper: 'Langue dans laquelle les reçus sont générés.',
      reviewLink: "Lien d'avis",
      reviewLinkHelper: 'Affiché aux clients après la soumission de leur avis.',
      earliestCheckIn: 'Arrivée au plus tôt',
      latestCheckIn: 'Arrivée au plus tard',
      earliestCheckout: 'Départ au plus tôt',
      latestCheckout: 'Départ au plus tard',
      codePrefix: "Préfixe de code de l'établissement",
      checkInWindow: "Plage d'arrivée",
      checkoutWindow: 'Plage de départ',
      windowRange: '{{start}} à {{end}}',
    },
    members: {
      title: 'Membres et invitations',
      subtitle: 'Personnel ayant accès à cet établissement, et invitations en attente.',
      refresh: 'Actualiser les membres',
      invite: 'Inviter un membre',
      loading: 'Chargement des membres…',
      error: 'Une erreur est survenue lors du chargement des membres. Veuillez réessayer.',
      empty: 'Aucun membre pour le moment.',
      membersCaption: 'Membres',
      roleForAria: 'Rôle de {{name}}',
      disableAria: 'Désactiver {{name}}',
      disable: 'Désactiver',
      enable: 'Réactiver',
      loadingInvitations: 'Chargement des invitations en attente…',
      errorInvitations: 'Une erreur est survenue lors du chargement des invitations en attente. Veuillez réessayer.',
      invitationsCaption: 'Invitations en attente',
      resendAria: "Renvoyer l'invitation à {{email}}",
      revokeAria: "Révoquer l'invitation de {{email}}",
      resend: "Renvoyer l'invitation",
      revoke: 'Révoquer',
      inviteTitle: 'Inviter un membre',
      sending: 'Envoi…',
      sendInvitation: "Envoyer l'invitation",
      memberFallback: 'Membre',
      roleChangedToast: '{{name}} est maintenant {{role}}.',
      roleChangeError: 'Impossible de modifier le rôle de ce membre.',
      invitationSentToast: 'Invitation envoyée à {{email}}.',
      inviteError: "Impossible d'envoyer cette invitation.",
      disabledToast: '{{name}} désactivé.',
      disableError: 'Impossible de désactiver ce membre.',
      enabledToast: '{{name}} réactivé.',
      enableError: 'Impossible de réactiver ce membre.',
      revokedToast: "Invitation de {{email}} révoquée.",
      revokeError: 'Impossible de révoquer cette invitation.',
      resentToast: 'Invitation renvoyée à {{email}}.',
      resendError: 'Impossible de renvoyer cette invitation.',
      pendingInvitation: 'invitation en attente',
      whatsappHelper: 'Par défaut, le numéro de téléphone est utilisé s\'il est laissé vide.',
    },
  },
  profile: {
    title: 'Mon profil',
    subtitle: 'Gérez les détails de votre compte et votre mot de passe.',
    accountDetails: 'Détails du compte',
    replacePicture: 'Remplacer la photo',
    uploadPicture: 'Téléverser une photo',
    takePhoto: 'Prendre une photo',
    remove: 'Retirer',
    noRole: 'Aucun rôle attribué pour le moment',
    platformAdmin: 'Administrateur de la plateforme',
    displayLanguage: "Langue d'affichage",
    emailReadonly: "L'e-mail ne peut pas être modifié ici.",
    roleHelperNamed: 'Votre rôle à {{name}}, défini par un administrateur.',
    roleHelper: 'Votre rôle est attribué par un administrateur.',
    changePassword: 'Changer le mot de passe',
    currentPassword: 'Mot de passe actuel',
    passwordHelper: 'Au moins 8 caractères, avec des lettres majuscules et minuscules et un chiffre.',
    updatePassword: 'Mettre à jour le mot de passe',
    updatingPassword: 'Mise à jour…',
    deactivate: 'Désactiver le compte',
    deactivateDesc: "La désactivation vous déconnecte partout et bloque la connexion jusqu'à ce qu'un administrateur réactive votre compte.",
    deactivateCta: 'Désactiver mon compte',
    deactivateTitle: 'Désactiver votre compte ?',
    deactivateBody: "Vous serez déconnecté immédiatement et ne pourrez pas vous reconnecter tant qu'un administrateur n'aura pas réactivé votre compte. Saisissez votre mot de passe pour confirmer.",
    deactivating: 'Désactivation…',
    deactivateConfirm: 'Désactiver le compte',
    cameraTitle: 'Prendre une photo de profil',
    profileUpdatedToast: 'Votre profil a été mis à jour.',
    profileUpdateError: 'Nous n\'avons pas pu mettre à jour votre profil. Veuillez réessayer.',
    photoUpdatedToast: 'Votre photo de profil a été mise à jour.',
    photoUpdateError: 'Nous n\'avons pas pu mettre à jour votre photo de profil. Veuillez réessayer.',
    photoRemovedToast: 'Votre photo de profil a été retirée.',
    photoRemoveError: 'Nous n\'avons pas pu retirer votre photo de profil. Veuillez réessayer.',
    deactivatedToast: 'Votre compte a été désactivé.',
    deactivateError: 'Nous n\'avons pas pu désactiver votre compte. Veuillez réessayer.',
    passwordUpdatedToast: 'Votre mot de passe a été mis à jour.',
    passwordUpdateError: 'Nous n\'avons pas pu mettre à jour votre mot de passe. Veuillez réessayer.',
  },
  notFound: {
    title: 'Page introuvable',
    message: "La page que vous recherchez n'existe pas ou a peut-être été déplacée.",
    backHome: "Retour à l'accueil",
    goDashboard: 'Aller au tableau de bord',
  },
  routeError: {
    title: 'Une erreur est survenue',
    message: "Une erreur inattendue s'est produite. Veuillez réessayer ou revenir à l'accueil.",
  },
  comingSoon: {
    eyebrow: 'BIENTÔT DISPONIBLE',
    title: 'Quelque chose de nouveau arrive',
    subtitle: "Nous apportons les dernières touches à la prochaine partie de Nang Book. Laissez votre e-mail et nous vous préviendrons dès que ce sera prêt.",
    onList: 'Vous êtes sur la liste',
    onListDetail: 'Nous enverrons un e-mail à <0>{{email}}</0> dès notre lancement.',
    emailAddress: 'Adresse e-mail',
    submitting: 'Inscription en cours…',
    notifyMe: 'Me prévenir au lancement',
    backHome: "Retour à l'accueil",
    enterEmail: 'Saisissez une adresse e-mail pour que nous puissions vous joindre.',
    invalidEmail: 'Saisissez une adresse e-mail valide.',
    subscribedToast: 'Vous êtes sur la liste — nous vous enverrons un e-mail au lancement.',
    genericError: 'Une erreur est survenue. Veuillez réessayer.',
  },
  legal: {
    lastUpdated: 'Dernière mise à jour : {{date}}',
    privacy: {
      eyebrow: 'Politique de confidentialité',
      title: 'Politique de confidentialité',
      intro: 'Cette politique de confidentialité explique comment Nang Book traite les informations lorsque les équipes des établissements utilisent la plateforme de gestion hôtelière.',
      s1Title: 'Informations que nous traitons',
      s1Body: "Nang Book peut traiter les détails du compte, les informations sur l'établissement, les coordonnées du personnel, les fiches clients et de réservation, le statut de paiement, les messages de support et les informations techniques d'utilisation nécessaires au fonctionnement de la plateforme.",
      s2Title: 'Comment les informations sont utilisées',
      s2Body: 'Nous utilisons les informations pour fournir les flux de gestion hôtelière, sécuriser l\'accès des utilisateurs, tenir les enregistrements de compte, soutenir les équipes des établissements, améliorer la fiabilité et respecter les obligations légales ou opérationnelles.',
      s3Title: 'Partage et prestataires de services',
      s3Body: "Les informations peuvent être partagées avec des prestataires qui aident à assurer l'hébergement, les e-mails, l'analyse, la sécurité, le support et les opérations liées aux paiements. Nous ne vendons pas les fiches clients ou visiteurs.",
      s4Title: 'Sécurité et conservation',
      s4Body: "Nous utilisons des mesures administratives et techniques conçues pour protéger les données. Les enregistrements ne sont conservés que le temps nécessaire au fonctionnement de la plateforme, à la conformité, à la résolution des litiges, aux sauvegardes et aux besoins commerciaux légitimes.",
      s5Title: 'Vos choix',
      s5Body: "Les administrateurs d'établissement peuvent gérer l'accès du personnel et demander la mise à jour ou la suppression des informations de compte éligibles. Certains enregistrements peuvent devoir être conservés lorsque cela est requis pour des raisons de sécurité, d'audit ou légales.",
    },
    terms: {
      eyebrow: 'Conditions générales',
      title: 'Conditions générales',
      intro: "Ces conditions générales décrivent les règles de base pour l'utilisation de Nang Book et la gestion de l'accès à la plateforme.",
      s1Title: 'Utilisation de la plateforme',
      s1Body: "Nang Book fournit des outils de gestion hôtelière basés sur le cloud pour les hôtels, maisons d'hôtes, lodges et hébergements de courte durée. Les utilisateurs ne doivent utiliser la plateforme que pour des opérations commerciales licites.",
      s2Title: 'Comptes et accès',
      s2Body: "Les clients sont responsables de la sécurité des identifiants de compte, de l'attribution d'un accès approprié au personnel, de la suppression des utilisateurs inactifs et de l'exactitude des informations saisies dans la plateforme.",
      s3Title: 'Données des clients et des visiteurs',
      s3Body: "Les équipes des établissements restent responsables des enregistrements qu'elles créent, téléversent ou gèrent dans Nang Book, y compris les avis, les autorisations et les bases légales requises pour les informations sur les clients, les réservations et les paiements.",
      s4Title: 'Utilisation acceptable',
      s4Body: "Les utilisateurs ne doivent pas abuser de la plateforme, tenter un accès non autorisé, interférer avec le fonctionnement du service, téléverser du contenu malveillant ou utiliser Nang Book pour enfreindre des lois, des droits ou des obligations contractuelles.",
      s5Title: 'Modifications du service',
      s5Body: "Nang Book peut mettre à jour les fonctionnalités, les flux de travail, les contrôles de sécurité et la documentation à mesure que le produit évolue. Les changements importants seront communiqués via des canaux produit ou de compte raisonnables.",
    },
  },
  onboarding: {
    verifyTitle: 'Vérifiez votre e-mail',
    verifySubtitle: 'Consultez votre boîte de réception pour un lien de vérification avant de continuer.',
    verifyAlert: 'Nous avons envoyé un lien de vérification à {{email}}. Ouvrez-le pour continuer.',
    resendVerification: "Renvoyer l'e-mail de vérification",
    welcome: 'Bienvenue, {{name}}',
    welcomeSubtitle: "Créez un établissement pour commencer, ou attendez une invitation du personnel pour en rejoindre un.",
    createProperty: 'Créer un établissement',
    invitedAlert: "Vous avez plutôt été invité à rejoindre un établissement existant ? Ouvrez le lien d'invitation depuis votre e-mail — il vous demandera de définir un mot de passe puis de vous connecter ici.",
    acceptedRefresh: "Je viens d'accepter une invitation — actualiser",
    createTitle: 'Créer votre établissement',
    createSubtitle: "Vous en serez le premier administrateur système, {{name}}.",
    creating: 'Création…',
    createSubmit: "Créer l'établissement",
    createdToast: 'Établissement créé.',
    createError: "Impossible de créer l'établissement.",
  },
  verifyReceipt: {
    title: 'Vérifier un reçu',
    subtitle: "Confirmez qu'un reçu est authentique — saisissez son numéro complet ou seulement les six derniers caractères.",
    inputLabel: 'Numéro de reçu ou 6 derniers caractères',
    verifying: 'Vérification…',
    verify: 'Vérifier',
    checking: 'Vérification du reçu…',
    notVerified: "Ce reçu n'a pas pu être vérifié.",
    authentic: 'Reçu authentique',
    receiptNumber: 'Numéro de reçu',
    amountExpected: 'Montant prévu',
    amountPaid: 'Montant payé',
    amountRemaining: 'Montant restant',
    booking: 'Réservation',
    issued: 'Émis',
    issuedBy: 'Émis par',
    property: 'Établissement',
  },
  bookingLink: {
    loading: 'Chargement de votre réservation…',
    invalidLink: "Ce lien est invalide, expiré ou a été révoqué. Veuillez contacter directement l'établissement si vous avez besoin d'aide pour votre réservation.",
    error: 'Une erreur est survenue lors du chargement de ce lien. Veuillez réessayer.',
    bookingTitle: 'Réservation {{code}}',
    shareFeedback: 'Partagez votre avis',
    thankYou: 'Merci — votre avis a été soumis.',
    rating: 'Note',
    submitting: 'Envoi…',
    submitReview: "Soumettre l'avis",
    reviewError: 'Impossible de soumettre votre avis. Veuillez réessayer.',
  },
  invitation: {
    loading: 'Chargement de votre invitation…',
    invalidInvite: "Cette invitation est invalide, expirée ou a déjà été acceptée. Demandez à la personne qui vous a invité d'en envoyer une nouvelle.",
    error: 'Une erreur est survenue lors du chargement de cette invitation. Veuillez réessayer.',
    joinTitle: 'Rejoindre {{name}}',
    joinFallback: "l'établissement",
    joinSubtitle: 'Confirmez vos détails et définissez un mot de passe pour accepter cette invitation.',
    acceptedNeedsLogin: "Invitation acceptée, mais la connexion automatique n'a pas abouti. Connectez-vous pour continuer.",
    goToLogin: 'Aller à la connexion',
    accepting: 'Acceptation…',
    acceptInvitation: "Accepter l'invitation",
    acceptedWelcome: 'Invitation acceptée. Bienvenue à {{name}}.',
    acceptedLogin: 'Invitation acceptée. Veuillez vous connecter pour continuer.',
    yourProperty: 'votre établissement',
    acceptError: 'Impossible d\'accepter cette invitation. Veuillez réessayer.',
  },
  platform: {
    title: 'Établissements de la plateforme',
    subtitle: 'Supervision des comptes inter-locataires, réservée aux administrateurs de la plateforme.',
    refresh: 'Actualiser les établissements',
    loading: 'Chargement des établissements…',
    error: 'Une erreur est survenue lors du chargement des établissements. Veuillez réessayer.',
    empty: 'Aucun établissement trouvé.',
    caption: 'Établissements',
    active: 'Actif',
    suspended: 'Suspendu',
    pageOf: 'Page {{current}} sur {{total}}',
    detail: {
      loading: "Chargement de l'établissement…",
      error: "Une erreur est survenue lors du chargement de cet établissement. Veuillez réessayer.",
      updating: 'Mise à jour…',
      suspendProperty: "Suspendre l'établissement",
      reactivateProperty: "Réactiver l'établissement",
      reactivatedToast: 'Établissement réactivé.',
      suspendedToast: 'Établissement suspendu.',
      statusError: "Impossible de mettre à jour le statut de cet établissement.",
    },
  },
  audit: {
    title: "Journal d'audit",
    subtitle: "Chaque action d'écriture effectuée dans cet espace de travail, réservée aux administrateurs système.",
    refresh: "Actualiser le journal d'audit",
    system: 'Système',
    actionContains: "L'action contient",
    resourceTypeContains: 'Le type de ressource contient',
    loading: "Chargement du journal d'audit…",
    error: "Une erreur est survenue lors du chargement du journal d'audit. Veuillez réessayer.",
    empty: 'Aucune entrée du journal ne correspond à ces filtres.',
    when: 'Quand',
    user: 'Utilisateur',
    action: 'Action',
    resource: 'Ressource',
    ipAddress: 'Adresse IP',
  },
  cleanup: {
    title: 'Nettoyage des données',
    subtitle: 'Fiches clients et réservations signalées comme incomplètes, corrigeables en masse.',
    guestsSection: 'Clients à nettoyer',
    bookingsSection: 'Réservations à nettoyer',
    loadingGuests: 'Chargement des clients…',
    guestsError: 'Une erreur est survenue lors du chargement des clients signalés.',
    guestsEmpty: "Aucun client n'a besoin de nettoyage pour le moment.",
    loadingBookings: 'Chargement des réservations…',
    bookingsError: 'Une erreur est survenue lors du chargement des réservations.',
    bookingsEmpty: "Aucune réservation n'a besoin de nettoyage pour le moment.",
    saveGuests: 'Enregistrer toutes les modifications clients',
    saveBookings: 'Enregistrer toutes les modifications de réservation',
    guestsSaved: '{{updated}} client(s) enregistré(s).',
    guestsSavedSome: '{{updated}} client(s) enregistré(s), {{failed}} échoué(s).',
    guestsSaveError: 'Impossible d\'enregistrer les modifications des clients.',
    bookingsSaved: '{{updated}} réservation(s) enregistrée(s).',
    bookingsSavedSome: '{{updated}} réservation(s) enregistrée(s), {{failed}} échouée(s).',
    bookingsSaveError: 'Impossible d\'enregistrer les modifications des réservations.',
    sectionGuest: 'Client',
    sectionBooking: 'Réservation',
    section: 'Section',
    code: 'Code',
    field1: 'Champ 1',
    field2: 'Champ 2',
    field3: 'Champ 3',
    field4: 'Champ 4',
    field5: 'Champ 5',
  },
  reports: {
    title: 'Rapports',
    subtitle: 'Rapports opérationnels issus des réservations, des finances et des avis.',
    reportLabel: 'Rapport',
    showUnderlying: 'Afficher les réservations sous-jacentes',
    loading: 'Chargement du rapport…',
    forbidden: "Vous n'avez pas la permission de consulter ce rapport.",
    error: 'Une erreur est survenue lors du chargement de ce rapport. Veuillez réessayer.',
    noMatchingBookings: 'Aucune réservation correspondante.',
    noData: 'Aucune donnée pour cette période.',
    total: 'Total',
    bookingDetail: '{{checkIn}} au {{checkOut}} · {{status}}',
    paymentsCount: '{{count}} paiement(s)',
    colSection: 'Section',
    colLabel: 'Libellé',
    colValue: 'Valeur',
    colDetail: 'Détail',
    names: {
      bookingSummary: 'Résumé des réservations (par statut)',
      occupancy: 'Occupation',
      revenue: 'Revenus',
      channel: 'Réservations par canal',
      cancelled: 'Réservations annulées',
      reviewedVsNot: 'Évaluées vs non évaluées',
      rating: 'Répartition des notes',
      purpose: 'Réservations par motif de séjour',
      nationality: 'Clients par nationalité',
      cleanup: 'Résumé des données à nettoyer',
      overduePayments: 'Paiements en retard',
      propertyFinancialBreakdown: "Répartition financière de l'établissement",
    },
    sections: {
      status: 'Statut',
      occupancy: 'Occupation',
      revenue: 'Revenus',
      channel: 'Canal',
      cancelled: 'Annulées',
      reviews: 'Avis',
      ratings: 'Notes',
      stayPurpose: 'Motif du séjour',
      nationality: 'Nationalité',
      cleanup: 'Nettoyage',
      overduePayments: 'Paiements en retard',
      financialBreakdown: 'Répartition financière',
      paymentMethod: 'Méthode de paiement',
      method: 'Méthode',
    },
    stats: {
      totalRoomNights: 'Total nuitées',
      totalOccupants: 'Total occupants',
      avgOccupants: 'Occupants moy. / réservation',
      bookingsCounted: 'Réservations comptées',
      expectedTotal: 'Total prévu',
      actualTotal: 'Total réel',
      actualTotalAgreed: 'Total réel (convenu)',
      collectedTotal: 'Total encaissé',
      recordsCounted: 'Enregistrements comptés',
      cancelledBookings: 'Réservations annulées',
      reviewed: 'Évaluées',
      notReviewed: 'Non évaluées',
      averageRating: 'Note moyenne',
      noRatings: 'Aucune note pour le moment',
      ratingsCounted: 'Notes comptées',
      guestsNeedingCleanup: 'Clients à nettoyer',
      bookingsNeedingCleanup: 'Réservations à nettoyer',
      overdueBookings: 'Réservations en retard',
      receivedTotal: 'Total reçu',
      outstanding: 'Restant dû',
      overpaid: 'Trop-perçu',
      paymentsCounted: 'Paiements comptés',
    },
    captions: {
      cancelledBookings: 'Réservations annulées',
      reviewedBookings: 'Réservations évaluées',
      notReviewedBookings: 'Réservations non évaluées',
      notYetReviewedBookings: 'Réservations pas encore évaluées',
      overdueBookings: 'Réservations en retard',
    },
    automation: {
      title: 'Automatisation des paiements',
      dryRun: 'Simulation',
      sendDigest: 'Envoyer le récapitulatif',
      autoCancelDays: 'Annuler automatiquement après (jours)',
      running: 'Exécution…',
      run: "Lancer l'automatisation",
      summaryDry: '{{overdue}} réservation(s) en retard détectée(s). {{cancelled}} réservation(s) seraient annulée(s) automatiquement.',
      summaryReal: '{{overdue}} réservation(s) en retard détectée(s). {{cancelled}} réservation(s) annulée(s) automatiquement.',
      invalidDays: "Le nombre de jours d'annulation automatique doit être un entier positif.",
      dryComplete: "Simulation de l'automatisation des paiements terminée.",
      complete: 'Automatisation des paiements terminée.',
      error: "Impossible d'exécuter l'automatisation des paiements.",
    },
  },
};
